import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  FileText,
  Truck,
  ClipboardCheck,
  Package,
  ArrowRightLeft,
  Undo2
} from "lucide-react";
import "./dashboard.css";
import BackButton from "../../components/backbutton";

export default function SecurityDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("list-po");
  const [showLogoutModal, setShowLogoutModal] = useState(false);


  // Sidebar menu items
  const tabItems = [
    { id: "list-po", label: "List PO", icon: <FileText size={18} /> },
    { id: "send-outsource-vendor", label: "Send Outsource to Vendor", icon: <Truck size={18} /> },
    { id: "grn-generation", label: "Goods Received Notes (GRN) Generation", icon: <ClipboardCheck size={18} /> },
    { id: "goods-received-outsource", label: "Goods Received from Outsource Vendor --> Entry", icon: <Package size={18} /> },
    { id: "output-entry", label: "Output Entry", icon: <ArrowRightLeft size={18} /> },
    { id: "sales-return-received", label: "Sales Return Received", icon: <Undo2 size={18} /> }
  ];

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    onLogout();
    navigate('/login');
  };
  const handleCancelLogout = () => setShowLogoutModal(false);

  const [poList] = useState([
    { id: 'PO-1024', supplier: 'Alpha Components', date: '2026-03-28', status: 'Quality Pending', qualityStatus: 'Entered to Quality', productMismatch: 'None', quantityMismatch: 'Shortage', excess: 0, shortage: 12, totalQty: 520 },
    { id: 'PO-1025', supplier: 'Orion Suppliers', date: '2026-03-25', status: 'Approved', qualityStatus: 'Complete', productMismatch: 'No', quantityMismatch: 'None', excess: 5, shortage: 0, totalQty: 735 },
    { id: 'PO-1026', supplier: 'Zenith Logistics', date: '2026-03-20', status: 'Review', qualityStatus: 'Entered to Quality', productMismatch: 'Yes', quantityMismatch: 'Excess', excess: 18, shortage: 0, totalQty: 220 }
  ]);
  const [selectedPo, setSelectedPo] = useState(poList[0]);

  const [grnList] = useState([
    { id: 'GRN-5401', poId: 'PO-1024', receivedDate: '2026-03-29', status: 'Pending', vendor: 'Alpha Components' },
    { id: 'GRN-5402', poId: 'PO-1025', receivedDate: '2026-03-26', status: 'Generated', vendor: 'Orion Suppliers' }
  ]);

  const [receivedGoods] = useState([
    { id: 'RCV-9001', vendor: 'Alpha Components', qualityStatus: 'Passed', totalItems: 125, mismatch: 'None' },
    { id: 'RCV-9002', vendor: 'Zenith Logistics', qualityStatus: 'Attention', totalItems: 78, mismatch: 'Product Mismatch' }
  ]);

  const [outputEntries] = useState([
    { id: 'OUT-3101', packaging: 'Packed', quantity: 420, status: 'Ready' },
    { id: 'OUT-3102', packaging: 'Partial', quantity: 120, status: 'Inspection' }
  ]);

  const [salesReturns] = useState([
    { id: 'RTN-7701', customer: 'Acme Traders', date: '2026-03-27', reason: 'Damaged', quantity: 15 },
    { id: 'RTN-7702', customer: 'Bright Retail', date: '2026-03-22', reason: 'Shortage', quantity: 8 }
  ]);

  const [grnInvoiceSupplier, setGrnInvoiceSupplier] = useState('Alpha Components');
  const [grnInvoiceNumber, setGrnInvoiceNumber] = useState('');
  const [outputInvoice, setOutputInvoice] = useState('');
  const [outputDc, setOutputDc] = useState('');
  const [outputEway, setOutputEway] = useState('');
  const [outputProducts, setOutputProducts] = useState([
    { id: 'P-001', name: 'Product 1', quantity: 120, verified: false },
    { id: 'P-002', name: 'Product 2', quantity: 80, verified: false }
  ]);

  const [salesReturnInvoice, setSalesReturnInvoice] = useState('');
  const [salesReturnDc, setSalesReturnDc] = useState('');
  const [salesReturnEway, setSalesReturnEway] = useState('');
  const [salesReturnProducts, setSalesReturnProducts] = useState([
    { id: 'SR-001', name: 'Returned Product 1', quantity: 15, verified: false },
    { id: 'SR-002', name: 'Returned Product 2', quantity: 8, verified: false }
  ]);

  // Get page title based on active tab
  const getPageTitle = () => {
    switch (activeTab) {
      case "list-po":
        return "List PO";
      case "send-outsource-vendor":
        return "Send Outsource to Vendor";
      case "grn-generation":
        return "Goods Received Notes (GRN) Generation";
      case "goods-received-outsource":
        return "Goods Received from Outsource Vendor --> Entry";
      case "output-entry":
        return "Output Entry";
      case "sales-return-received":
        return "Sales Return Received";
      default:
        return "Security Dashboard";
    }
  };

  const selectPo = (po) => setSelectedPo(po);

  const renderListPo = () => {
    const qualityCount = poList.filter((po) => po.qualityStatus === 'Entered to Quality').length;
    const mismatchCount = poList.filter((po) => po.productMismatch === 'Yes').length;
    const shortageCount = poList.filter((po) => po.quantityMismatch === 'Shortage').length;
    const excessCount = poList.filter((po) => po.quantityMismatch === 'Excess').length;

    return (
      <div>
        <div className="security-summary-grid">
          <div className="security-card">
            <h3>Total POs</h3>
            <p>{poList.length}</p>
          </div>
          <div className="security-card">
            <h3>Entered to Quality</h3>
            <p>{qualityCount}</p>
          </div>
          <div className="security-card">
            <h3>Product Mismatch</h3>
            <p>{mismatchCount}</p>
          </div>
          <div className="security-card">
            <h3>Quantity Shortage</h3>
            <p>{shortageCount}</p>
          </div>
          <div className="security-card">
            <h3>Quantity Excess</h3>
            <p>{excessCount}</p>
          </div>
        </div>

        <div className="security-table-panel">
          <div className="security-table-card">
            <div className="section-header">
              <h2 className="section-title">PO List</h2>
            </div>
            <table className="security-table">
              <thead>
                <tr>
                  <th>PO ID</th>
                  <th>Supplier</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Quality</th>
                  <th>Mismatch</th>
                </tr>
              </thead>
              <tbody>
                {poList.map((po) => (
                  <tr key={po.id} className={selectedPo?.id === po.id ? 'selected-row' : ''} onClick={() => selectPo(po)}>
                    <td>{po.id}</td>
                    <td>{po.supplier}</td>
                    <td>{po.date}</td>
                    <td><span className={`status-pill ${po.status === 'Approved' ? 'success' : po.status === 'Review' ? 'warning' : 'info'}`}>{po.status}</span></td>
                    <td>{po.qualityStatus}</td>
                    <td>{po.quantityMismatch}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="security-detail-card">
            <h3>PO Details</h3>
            {selectedPo ? (
              <div>
                <p><strong>PO ID:</strong> {selectedPo.id}</p>
                <p><strong>Supplier:</strong> {selectedPo.supplier}</p>
                <p><strong>Date:</strong> {selectedPo.date}</p>
                <p><strong>Total Qty:</strong> {selectedPo.totalQty}</p>
                <p><strong>Shortage:</strong> {selectedPo.shortage}</p>
                <p><strong>Excess:</strong> {selectedPo.excess}</p>
                <p><strong>Product Mismatch:</strong> {selectedPo.productMismatch}</p>
                <p><strong>Quality Status:</strong> {selectedPo.qualityStatus}</p>
              </div>
            ) : (
              <p>Select a PO to view details.</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSendOutsourceVendor = () => (
    <div>
      <div className="section-header">
        <h2 className="section-title">Send Outsource to Vendor</h2>
      </div>
      <div className="security-action-card">
        <p>Prepare and dispatch selected production orders to outsource vendors with required delivery instructions.</p>
      </div>
      <div className="security-table-card">
        <table className="security-table">
          <thead>
            <tr>
              <th>PO ID</th>
              <th>Vendor</th>
              <th>Status</th>
              <th>Expected Dispatch</th>
            </tr>
          </thead>
          <tbody>
            {poList.map((po) => (
              <tr key={po.id}>
                <td>{po.id}</td>
                <td>{po.supplier}</td>
                <td><span className="status-pill info">Ready</span></td>
                <td>{po.date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderGrnGeneration = () => {
    const currentPo = poList[0];

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Goods Received Notes (GRN) Generation</h2>
        </div>

        <div className="grn-layout">
          <div className="security-table-card grn-card">
            <h3>Purchase Order Details</h3>
            <ul className="grn-detail-list">
              <li><strong>Entered to Quality</strong></li>
              <li><strong>Product Mismatch</strong></li>
              <li><strong>Quantity Mismatch</strong></li>
              <li><strong>Excess → Quantity</strong></li>
              <li><strong>Shortage → Quantity</strong></li>
            </ul>

            <div className="grn-form-group">
              <label>Supplier</label>
              <select
                value={grnInvoiceSupplier}
                onChange={(e) => setGrnInvoiceSupplier(e.target.value)}
              >
                {poList.map((po) => (
                  <option key={po.id} value={po.supplier}>{po.supplier}</option>
                ))}
              </select>
            </div>

            <div className="grn-form-group">
              <label>Supplier Invoice Number</label>
              <input
                type="text"
                value={grnInvoiceNumber}
                onChange={(e) => setGrnInvoiceNumber(e.target.value)}
                placeholder="Enter invoice number"
              />
            </div>

            <button className="submit-btn">Submit</button>
          </div>

          <div className="security-detail-card grn-info-card">
            <h3>Selected PO</h3>
            <p><strong>PO ID:</strong> {currentPo.id}</p>
            <p><strong>Supplier:</strong> {currentPo.supplier}</p>
            <p><strong>Status:</strong> {currentPo.status}</p>
            <p><strong>Quality:</strong> {currentPo.qualityStatus}</p>
            <p><strong>Qty Shortage:</strong> {currentPo.shortage}</p>
            <p><strong>Qty Excess:</strong> {currentPo.excess}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderGoodsReceivedOutsource = () => (
    <div>
      <div className="section-header">
        <h2 className="section-title">Goods Received from Outsource Vendor</h2>
      </div>
      <div className="security-table-card">
        <table className="security-table">
          <thead>
            <tr>
              <th>Receipt ID</th>
              <th>Vendor</th>
              <th>Quality Status</th>
              <th>Total Items</th>
              <th>Mismatch</th>
            </tr>
          </thead>
          <tbody>
            {receivedGoods.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.vendor}</td>
                <td>{item.qualityStatus}</td>
                <td>{item.totalItems}</td>
                <td>{item.mismatch}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderOutputEntry = () => {
    const toggleProductVerified = (index) => {
      setOutputProducts((prev) =>
        prev.map((product, idx) =>
          idx === index ? { ...product, verified: !product.verified } : product
        )
      );
    };

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Output Entry</h2>
        </div>

        <div className="output-layout">
          <div className="security-table-card output-form-card">
            <div className="grn-form-group">
              <label>Select Invoice</label>
              <select value={outputInvoice} onChange={(e) => setOutputInvoice(e.target.value)}>
                <option value="">Choose invoice</option>
                <option value="INV-1201">INV-1201</option>
                <option value="INV-1202">INV-1202</option>
              </select>
            </div>
            <div className="grn-form-group">
              <label>Select DC</label>
              <select value={outputDc} onChange={(e) => setOutputDc(e.target.value)}>
                <option value="">Choose DC</option>
                <option value="DC-5581">DC-5581</option>
                <option value="DC-5582">DC-5582</option>
              </select>
            </div>
            <div className="grn-form-group">
              <label>Select Eway Bill</label>
              <select value={outputEway} onChange={(e) => setOutputEway(e.target.value)}>
                <option value="">Choose Eway Bill</option>
                <option value="EW-8871">EW-8871</option>
                <option value="EW-8872">EW-8872</option>
              </select>
            </div>

            <div className="product-list-card">
              <h3>List Products</h3>
              {outputProducts.map((product, idx) => (
                <div key={product.id} className="product-row">
                  <div>
                    <strong>{product.name}</strong>
                    <p>Quantity: {product.quantity}</p>
                  </div>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={product.verified}
                      onChange={() => toggleProductVerified(idx)}
                    />
                    Verify
                  </label>
                </div>
              ))}
            </div>

            <button className="submit-btn">Submit</button>
          </div>
        </div>
      </div>
    );
  };

  const renderSalesReturnReceived = () => {
    const toggleReturnVerified = (index) => {
      setSalesReturnProducts((prev) =>
        prev.map((product, idx) =>
          idx === index ? { ...product, verified: !product.verified } : product
        )
      );
    };

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Sales Return Received</h2>
        </div>

        <div className="output-layout">
          <div className="security-table-card output-form-card">
            <div className="grn-form-group">
              <label>Select Invoice</label>
              <select value={salesReturnInvoice} onChange={(e) => setSalesReturnInvoice(e.target.value)}>
                <option value="">Choose invoice</option>
                <option value="SRINV-2101">SRINV-2101</option>
                <option value="SRINV-2102">SRINV-2102</option>
              </select>
            </div>
            <div className="grn-form-group">
              <label>Select DC</label>
              <select value={salesReturnDc} onChange={(e) => setSalesReturnDc(e.target.value)}>
                <option value="">Choose DC</option>
                <option value="SRDC-3301">SRDC-3301</option>
                <option value="SRDC-3302">SRDC-3302</option>
              </select>
            </div>
            <div className="grn-form-group">
              <label>Select Eway Bill</label>
              <select value={salesReturnEway} onChange={(e) => setSalesReturnEway(e.target.value)}>
                <option value="">Choose Eway Bill</option>
                <option value="SREW-4411">SREW-4411</option>
                <option value="SREW-4412">SREW-4412</option>
              </select>
            </div>

            <div className="product-list-card">
              <h3>List Products</h3>
              {salesReturnProducts.map((product, idx) => (
                <div key={product.id} className="product-row">
                  <div>
                    <strong>{product.name}</strong>
                    <p>Quantity: {product.quantity}</p>
                  </div>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={product.verified}
                      onChange={() => toggleReturnVerified(idx)}
                    />
                    Verify
                  </label>
                </div>
              ))}
            </div>

            <button className="submit-btn">Submit</button>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "list-po":
        return renderListPo();
      case "send-outsource-vendor":
        return renderSendOutsourceVendor();
      case "grn-generation":
        return renderGrnGeneration();
      case "goods-received-outsource":
        return renderGoodsReceivedOutsource();
      case "output-entry":
        return renderOutputEntry();
      case "sales-return-received":
        return renderSalesReturnReceived();
      default:
        return renderListPo();
    }
  };

  return (
    <div className="security-dashboard-container">
      {/* Sidebar */}
      <div className="security-sidebar">
        <div className="security-logo-container">
          <div className="security-logo-icon">
            <img 
              src="/rahulerp.jpg" 
              alt="Company Logo" 
              className="security-logo-image"
            />
          </div>
          <div>
            <h2 className="security-company-name">Ragul Auto Tech</h2>
            <p className="security-company-tagline">AI Powered ERP</p>
          </div>
        </div>

        <nav className="security-nav-menu">
          {tabItems.map((tab) => (
            <button
              key={tab.id}
              className={`security-nav-item ${activeTab === tab.id ? 'security-nav-item-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="security-nav-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="security-logout-container">
          <button onClick={handleLogoutClick} className="security-logout-button">
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="security-main-content">
        {/* Header */}
        <header className="security-header">
          <div>
            <h1 className="security-main-title">Security Dashboard</h1>
            <div className="security-subtitle">
              Welcome, {user?.role || 'Security Manager'}
            </div>
          </div>

          <div className="security-header-actions">
            <div className="security-last-updated">Last updated: {new Date().toLocaleString()}</div>
          </div>
        </header>
        <BackButton />

        {/* Content Area */}
        <div className="security-content-card">
          {renderContent()}
        </div>
        {/* Logout Confirmation Modal */}
        {showLogoutModal && (
          <div className="logout-modal-overlay">
            <div className="logout-modal">
              <div className="logout-modal-header">
                <div className="logout-modal-icon">👋</div>
                <h3>Confirm Logout</h3>
              </div>
              <div className="logout-modal-body">
                <p>Are you sure you want to logout from <strong>Security</strong> dashboard?</p>
                <p className="logout-modal-warning">You will be redirected to the login page.</p>
              </div>
              <div className="logout-modal-footer">
                <button className="logout-modal-cancel" onClick={handleCancelLogout}>Cancel</button>
                <button className="logout-modal-confirm" onClick={handleConfirmLogout}>Yes, Logout</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}